/**
 * One compiled field, drawn as the control its type calls for.
 *
 * Every branch here is decided by data that came from the product, not by
 * anything this file knows about insurance. Adding a field to a product adds a
 * control to the form; nothing in the frontend is edited or redeployed.
 *
 * The one judgement worth stating: a yes/no field is a select with an empty
 * option rather than a checkbox, because a checkbox cannot tell "no" from
 * "not answered yet", and on an application form that difference matters.
 */
import type { CompiledField } from "@dp/shared";

export function Control({
  field, value, disabled, wrong, onChange,
}: {
  field: CompiledField;
  value: unknown;
  disabled: boolean;
  wrong: boolean;
  onChange: (value: unknown) => void;
}) {
  const className = `control${wrong ? " is-wrong" : ""}`;
  const common = { id: field.key, className, disabled, "aria-invalid": wrong || undefined };
  const text = value === null || value === undefined ? "" : String(value);

  switch (field.dataType) {
    case "long_text":
      return <textarea {...common} rows={3} value={text} onChange={(event) => onChange(event.target.value)} />;

    case "boolean":
      return (
        <select {...common} value={value === true ? "true" : value === false ? "false" : ""}
          onChange={(event) => onChange(event.target.value === "" ? null : event.target.value === "true")}>
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );

    case "date":
      return <input {...common} type="date" value={text} onChange={(event) => onChange(event.target.value)} />;

    case "choice":
      return (
        <select {...common} value={text} onChange={(event) => onChange(event.target.value || null)}>
          <option value="">—</option>
          {(field.options ?? []).map((option) => (
            <option key={option.code} value={option.code}>{option.label}</option>
          ))}
        </select>
      );

    case "multi_choice": {
      const chosen = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <div className="chips">
          {(field.options ?? []).map((option) => (
            <label key={option.code} className={`chip picky${chosen.has(option.code) ? " chip-accent" : ""}`}>
              <input
                type="checkbox" checked={chosen.has(option.code)} disabled={disabled}
                onChange={(event) => {
                  const next = new Set(chosen);
                  if (event.target.checked) next.add(option.code);
                  else next.delete(option.code);
                  onChange([...next]);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    }

    case "integer":
    case "decimal":
    case "money":
    case "percent": {
      const step = field.dataType === "integer" ? 1 : field.validation?.precision ? 10 ** -field.validation.precision : "any";
      return (
        <div className={field.dataType === "money" ? "with-prefix" : field.dataType === "percent" ? "with-suffix" : undefined}>
          {field.dataType === "money" && <span className="affix">$</span>}
          <input
            {...common} type="number" inputMode="decimal" step={step} value={text}
            min={field.validation?.min ?? undefined}
            max={field.validation?.max ?? undefined}
            onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
          />
          {field.dataType === "percent" && <span className="affix">%</span>}
        </div>
      );
    }

    case "email":
      return <input {...common} type="email" value={text} onChange={(event) => onChange(event.target.value)} />;

    case "phone":
      return <input {...common} type="tel" value={text} onChange={(event) => onChange(event.target.value)} />;

    default:
      return (
        <input
          {...common} type="text" value={text}
          maxLength={field.validation?.maxLength ?? undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}
